import {
    associateWebAuthnCredential,
    deleteWebAuthnCredential,
    listWebAuthnCredentials,
    signIn,
    type SignInOutput,
} from "aws-amplify/auth";
import { supportsPasskeys } from "./authHelpers.ts";

/** A registered WebAuthn credential, as returned by Amplify. */
export type Passkey = Awaited<
    ReturnType<typeof listWebAuthnCredentials>
>["credentials"][number];

/**
 * Whether passkeys can be offered: the browser must speak WebAuthn, and the
 * auth backend must implement it (cognito-local doesn't — see
 * `supportsPasskeys`). Callers use this to hide the passkey Ui entirely.
 */
export function isPasskeySupported(): boolean {
    return (
        supportsPasskeys &&
        typeof window !== "undefined" &&
        typeof window.PublicKeyCredential !== "undefined"
    );
}

/**
 * Passwordless sign-in with a passkey via Cognito's choice-based USER_AUTH
 * flow, preferring the WebAuthn factor. Triggers the browser passkey ceremony.
 */
export function signInWithPasskey(email: string): Promise<SignInOutput> {
    return signIn({
        username: email.trim(),
        options: {
            authFlowType: "USER_AUTH",
            preferredChallenge: "WEB_AUTHN",
        },
    });
}

/** Register a new passkey for the currently signed-in user. */
export function registerPasskey(): Promise<void> {
    return associateWebAuthnCredential();
}

export async function listPasskeys(): Promise<Passkey[]> {
    const { credentials } = await listWebAuthnCredentials();
    return credentials;
}

export function deletePasskey(credentialId: string): Promise<void> {
    return deleteWebAuthnCredential({ credentialId });
}
